import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getAd, assignAdToUser } from '../api/videos';
import { DailyStatsChart } from '../components/admin/DailyStatsChart';
import { getUsers } from '../services/admin';
import type { User } from '../types/admin';

interface AdminAd {
  _id: string;
  title: string;
  description?: string;
  creativeType?: 'text' | 'image' | 'video';
  imageUrl?: string;
  videoUrl?: string;
  landingUrl?: string;
  categories?: string[];
  tags?: string[];
  views?: number;
  clicks?: number;
  owner?: {
    id: string;
    email?: string;
    username?: string;
  };
  analytics?: {
    telemetrySamples?: number;
    telemetryBlankEvents?: number;
    social?: {
      totalClicks?: number;
      bySource?: Record<string, number>;
    };
  };
}

type MetricKey = 'views' | 'clicks' | 'telemetryBlankEvents';

const AdminAdDetailPage: React.FC = () => {
  const params = useParams();
  const adId = params.id as string | undefined;

  const [ad, setAd] = useState<AdminAd | null>(null);
  const [metric, setMetric] = useState<MetricKey>('views');
  const [timeRange, setTimeRange] = useState<'all' | '7d' | '30d'>('30d');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [assignTargetUserId, setAssignTargetUserId] = useState('');
  const [assignStatus, setAssignStatus] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(true);
  const [usersError, setUsersError] = useState<string | null>(null);

  useEffect(() => {
    if (!adId) {
      setError('Missing ad id');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = (await getAd(adId)) as AdminAd;
        if (!cancelled) {
          setAd(data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const msg =
            err &&
            typeof err === 'object' &&
            'message' in err &&
            typeof (err as { message?: unknown }).message === 'string'
              ? ((err as { message?: unknown }).message as string)
              : 'Failed to load ad';
          setError(msg);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [adId]);

  useEffect(() => {
    let cancelled = false;

    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const fetched = await getUsers();
        if (!cancelled) {
          setUsers(fetched);
        }
      } catch {
        if (!cancelled) {
          setUsersError('Failed to load users');
        }
      } finally {
        if (!cancelled) {
          setUsersLoading(false);
        }
      }
    };

    void loadUsers();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleAssignToUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setAssignStatus(null);
    if (!adId) {
      setAssignStatus('Missing ad id');
      return;
    }
    if (!assignTargetUserId.trim()) {
      setAssignStatus('Enter a target user id');
      return;
    }

    try {
      const updated = await assignAdToUser(adId, assignTargetUserId.trim());
      setAd(updated as AdminAd);
      setAssignStatus('Assigned to user successfully.');
    } catch (err: unknown) {
      const msg =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message?: unknown }).message === 'string'
          ? ((err as { message?: unknown }).message as string)
          : 'Failed to assign ad to user';
      setAssignStatus(msg);
    }
  };

  const totalViews = typeof ad?.views === 'number' ? ad.views : 0;
  const totalClicks = typeof ad?.clicks === 'number' ? ad.clicks : 0;
  const totalBlanks = ad?.analytics?.telemetryBlankEvents ?? 0;
  const totalSamples = ad?.analytics?.telemetrySamples ?? 0;
  const ctr = totalViews > 0 ? (totalClicks / totalViews) * 100 : null;

  const ownerLabel = ad?.owner ? ad.owner.username || ad.owner.email || ad.owner.id : 'Unassigned';

  const assignHelpText = useMemo(() => {
    if (usersLoading) {
      return 'Loading users…';
    }
    if (usersError) {
      return usersError;
    }
    if (!users.length) {
      return 'No users available to assign this ad to yet.';
    }
    return 'Choose a user profile; this ad will appear in their TELLURVISION feed.';
  }, [usersLoading, usersError, users.length]);

  const dailyRangeFrom: string | undefined = (() => {
    const now = new Date();
    if (timeRange === '7d') {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      return d.toISOString().slice(0, 10);
    }
    if (timeRange === '30d') {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return d.toISOString().slice(0, 10);
    }
    return undefined;
  })();

  return (
    <main className="mx-auto max-w-6xl px-4 py-8 space-y-6">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">Ad details</h1>
          {ad && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{ad.title}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin"
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Back to admin
          </Link>
          <Link
            to="/"
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Go home
          </Link>
        </div>
      </header>

      {loading && <p className="text-sm text-slate-500 dark:text-slate-400">Loading ad…</p>}
      {error && !loading && <p className="text-sm text-red-600">{error}</p>}

      {ad && !loading && !error && (
        <section className="space-y-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Summary</h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Key details and lifetime performance for this ad.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-slate-800 dark:text-slate-200 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Title</div>
                <div className="mt-0.5 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {ad.title || 'Untitled'}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Current owner</div>
                <div className="mt-0.5 text-xs text-slate-700 dark:text-slate-300">
                  {ad.owner ? (
                    <Link
                      to={`/admin/users/${ad.owner.id}`}
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      <span>{ownerLabel}</span>
                      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                        View business analytics
                      </span>
                    </Link>
                  ) : (
                    ownerLabel
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Categories</div>
                <div className="mt-0.5 text-xs text-slate-700 dark:text-slate-300">
                  {ad.categories?.join(', ') || 'None'}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Tags</div>
                <div className="mt-0.5 text-xs text-slate-700 dark:text-slate-300">{ad.tags?.join(', ') || 'None'}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Creative type</div>
                <div className="mt-0.5 text-xs text-slate-700 dark:text-slate-300 capitalize">
                  {ad.creativeType ?? (ad.videoUrl ? 'video' : ad.imageUrl ? 'image' : 'text')}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Description</div>
                <div className="mt-0.5 text-xs text-slate-700 dark:text-slate-300 line-clamp-3">
                  {ad.description || 'No description'}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Landing URL</div>
                <div className="mt-0.5 break-all text-xs text-slate-700 dark:text-slate-300">
                  {ad.landingUrl || 'Not set'}
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-slate-800 dark:text-slate-200 md:grid-cols-4">
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Total views</div>
                <div className="mt-0.5 text-lg font-semibold text-slate-900 dark:text-slate-100">{totalViews}</div>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  How many times this ad was shown on screen.
                </p>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Total clicks</div>
                <div className="mt-0.5 text-lg font-semibold text-slate-900 dark:text-slate-100">{totalClicks}</div>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  How many times viewers clicked through from this ad.
                </p>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">CTR</div>
                <div className="mt-0.5 text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {ctr !== null ? `${ctr.toFixed(1)}%` : '–'}
                </div>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Click-through rate: clicks divided by views.
                </p>
              </div>
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Safety blanks / samples</div>
                <div className="mt-0.5 text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {totalBlanks} / {totalSamples}
                </div>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  A "safety blank" is when this ad was hidden instead of shown because telemetry
                  flagged a higher-risk moment (for example, high speed).
                </p>
              </div>
            </div>
            <form
              onSubmit={handleAssignToUser}
              className="mt-4 flex flex-col gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-xs text-slate-700 dark:text-slate-300 sm:flex-row sm:items-end sm:gap-3"
            >
              <div className="flex-1">
                <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400">
                  Send this ad to a user profile
                </label>
                <select
                  value={assignTargetUserId}
                  onChange={(e) => setAssignTargetUserId(e.target.value)}
                  disabled={usersLoading || !!usersError || !users.length}
                  className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">
                    {usersLoading
                      ? 'Loading users…'
                      : usersError
                        ? 'Unable to load users'
                        : 'Select a user…'}
                  </option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email} ({u.email})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{assignHelpText}</p>
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                >
                  Send to user profile
                </button>
              </div>
              {assignStatus && (
                <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-400 sm:mt-0">{assignStatus}</p>
              )}
            </form>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Daily trend</h2>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Per-day{' '}
                  {metric === 'views' ? 'views' : metric === 'clicks' ? 'clicks' : 'safety blanks'}{' '}
                  for this ad.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <label className="flex items-center gap-1">
                  Metric:
                  <select
                    value={metric}
                    onChange={(e) =>
                      setMetric(
                        e.target.value === 'clicks'
                          ? 'clicks'
                          : e.target.value === 'telemetryBlankEvents'
                            ? 'telemetryBlankEvents'
                            : 'views',
                      )
                    }
                    className="rounded border border-slate-300 dark:border-slate-600 px-1.5 py-0.5 text-xs"
                  >
                    <option value="views">Views</option>
                    <option value="clicks">Clicks</option>
                    <option value="telemetryBlankEvents">Safety blanks</option>
                  </select>
                </label>
                <label className="flex items-center gap-1">
                  Range:
                  <select
                    value={timeRange}
                    onChange={(e) =>
                      setTimeRange(
                        e.target.value === '7d' ? '7d' : e.target.value === '30d' ? '30d' : 'all',
                      )
                    }
                    className="rounded border border-slate-300 dark:border-slate-600 px-1.5 py-0.5 text-xs"
                  >
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="all">All time</option>
                  </select>
                </label>
              </div>
            </div>
            <DailyStatsChart adId={ad._id} metric={metric} from={dailyRangeFrom} />
          </div>
        </section>
      )}
    </main>
  );
};

export default AdminAdDetailPage;
